import { Coordinate, type MapData, type MapView } from "@mappedin/mappedin-js";
import { config } from "./config";
import {
  allCategories,
  buildDirectory,
  buildExitDirectory,
  byCategory,
  categoryIconMap,
  findAnchorTarget,
  findKioskAnchor,
  isLiveLocationAnchor,
  isStandEntry,
  resolveLocalCategoryIcons,
  resolveLocalDirectoryAssets,
  routeOriginTarget,
  searchDirectory,
  searchExits,
  type ExitAnchor,
  type RouteOrigin,
  type StandEntry,
} from "./directory";
import {
  buildDetailedInstructions,
  type DirectionIconType,
} from "./directions-text";
import {
  addExhibitorLabels,
  addKioskMarker,
  applyCameraPreset,
  applyStandColorRanges,
  captureCurrentCameraAsPreset,
  captureVenuePresets,
  clearDestinationMarker,
  clearPOIMarkers,
  clearRoute,
  enableInteractivity,
  findPOIsAlongRoute,
  focusOnPOI,
  focusWholeVenue,
  getFloors,
  highlightZone,
  initMap,
  onFloorChange,
  onSpaceClick,
  renderKioskPointerMarkers,
  renderLocationLogos,
  renderLocationPhotos,
  resetNorth,
  rotateCamera,
  routeBetween,
  setCameraRotationLock,
  setDestinationMarker,
  setPitchMode,
  renderExitMarkers,
  findNearestExit,
  onRouteIntroPitchChange,
  setStandHitRings,
  startLiveLocationTracking,
  metersBetween,
  switchFloor,
  updateHoverColors,
  zoneColorForCategory,
  zoomCamera,
  type AlongRoutePOI,
  type LiveLocationErrorKind,
} from "./mappedin";
import {
  buildPOIDirectory,
  getCategoryLabel,
  getCategoryColor,
  getCategorySVG,
  type POIEntry,
} from "./pois";
import { IdleManager } from "./ui/idle";
import { OnScreenKeyboard } from "./ui/keyboard";
import {
  getLocale,
  initLocale,
  setLocale,
  SUPPORTED_LOCALES,
  t,
  type Dictionary,
  type Locale,
} from "./i18n";
import { OccupancyService, type OccupancyLevel } from "./occupancy";
import type { Floor, PointOfInterest, Space } from "@mappedin/mappedin-js";
// Self-hosted (via @fontsource/inter) instead of the Google Fonts CDN link
// this used to be — the kiosk needs to boot with no internet at all, and a
// stylesheet fetched live from fonts.googleapis.com can't do that.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "./style.css";

type FieldName = "from" | "to";

// --- DOM handles -----------------------------------------------------------

const $ = <T extends Element = HTMLElement>(selector: string): T => {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Missing element: ${selector}`);
  return el;
};

const mapContainer = $<HTMLDivElement>("#map");
const mapLoader = $<HTMLDivElement>("#map-loader");
const mapLoaderTextEl = $<HTMLSpanElement>("#map-loader-text");
const idleOverlay = $<HTMLDivElement>("#idle-overlay");
const idleVideoWrap = $<HTMLDivElement>("#idle-video-wrap");
const idleVideo = $<HTMLVideoElement>("#idle-video");
const errorBanner = $<HTMLDivElement>("#error-banner");
const errorTextEl = $<HTMLSpanElement>("#error-text");

// Header
const venueNameTextEl = $<HTMLSpanElement>("#venue-name-text");
const kioskLabelBadgeEl = $<HTMLDivElement>("#kiosk-label-badge");
const kioskLabelTextEl = $<HTMLSpanElement>("#kiosk-label-text");
const clockEl = $<HTMLDivElement>("#clock");
const cameraDebugEl = $<HTMLDivElement>("#camera-debug");
const homeBtn = $<HTMLButtonElement>("#home-btn");
const themeBtn = $<HTMLButtonElement>("#theme-btn");
const reloadBtn = $<HTMLButtonElement>("#reload-btn");
const bottomSearchBtn = $<HTMLButtonElement>("#bottom-search");
const nearestExitBtn = $<HTMLButtonElement>("#nearest-exit-btn");
const toFieldEl = $<HTMLDivElement>("#to-field");
const bottomSearchPlaceholderEl = $<HTMLSpanElement>("#bottom-search-placeholder");
const bottomSearchValueEl = $<HTMLSpanElement>("#bottom-search-value");

// Camera / Map controls
const mapControlsEl = $<HTMLDivElement>("#map-controls");
const compassBtn = $<HTMLButtonElement>("#compass-btn");
const rotateControlsEl = $<HTMLDivElement>("#rotate-controls");
const compassNeedle = $<SVGElement>("#compass-needle");
const tiltBtn = $<HTMLButtonElement>("#tilt-btn");
const tiltLabel = $<HTMLSpanElement>("#tilt-label");
const rotateLeftBtn = $<HTMLButtonElement>("#rotate-left-btn");
const rotateRightBtn = $<HTMLButtonElement>("#rotate-right-btn");
const zoomInBtn = $<HTMLButtonElement>("#zoom-in-btn");
const zoomOutBtn = $<HTMLButtonElement>("#zoom-out-btn");
const recenterBtn = $<HTMLButtonElement>("#recenter-btn");
const lockBtn = $<HTMLButtonElement>("#lock-btn");
const floorPickerEl = $<HTMLDivElement>("#floor-picker");

// Connection status badge
const connectionBadgeEl = $<HTMLDivElement>("#connection-badge");
const connectionTextEl = $<HTMLSpanElement>("#connection-text");

// Live GPS "You Are Here" accuracy badge (visitor's own phone only)
const liveLocationBadgeEl = $<HTMLDivElement>("#live-location-badge");
const liveLocationTextEl = $<HTMLSpanElement>("#live-location-text");

// Header overflow menu (mobile only — see style.css's "display: contents" trick)
const headerEl = $<HTMLElement>("#header");
const headerMenuBtn = $<HTMLButtonElement>("#header-menu-btn");

// Language switcher
const langSwitcherEl = $<HTMLDivElement>("#lang-switcher");
const langBtn = $<HTMLButtonElement>("#lang-btn");
const langBtnCodeEl = $<HTMLSpanElement>("#lang-btn-code");
const langMenuEl = $<HTMLDivElement>("#lang-menu");

// Wayfinding panel
const categoryChipsEl = $<HTMLDivElement>("#category-chips");
const searchRowEl = $<HTMLDivElement>("#search-row");
const searchTabsEl = $<HTMLDivElement>("#search-tabs");
const tabListingBtn = $<HTMLButtonElement>("#tab-listing");
const tabKeyboardBtn = $<HTMLButtonElement>("#tab-keyboard");
const panelEl = $<HTMLElement>("#panel");
const drawerHandleEl = $<HTMLDivElement>(".drawer-handle");
const mobilePickerToggleBtn = $<HTMLButtonElement>("#mobile-picker-toggle");
const suggestionsEl = $<HTMLUListElement>("#suggestions");
const keyboardAreaEl = $<HTMLDivElement>("#keyboard-area");
const suggestionsFlyoutEl = $<HTMLDivElement>("#suggestions-flyout");
const goBtn = $<HTMLButtonElement>("#go-btn");
const swapBtn = $<HTMLButtonElement>("#swap-btn");

// Directions result
const resultSection = $<HTMLElement>("#directions-result");
const resultDistanceEl = $<HTMLDivElement>("#result-distance");
const resultStepsEl = $<HTMLSpanElement>("#result-steps");
const resultEtaEl = $<HTMLSpanElement>("#result-eta");
const resultInstructionsEl = $<HTMLOListElement>("#result-instructions");
const stepsToggleBtn = $<HTMLButtonElement>("#steps-toggle");
const clearRouteBtn = $<HTMLButtonElement>("#clear-route-btn");
const alongRouteSection = $<HTMLDivElement>("#along-route-section");

// QR share modal
const shareRouteBtn = $<HTMLButtonElement>("#share-route-btn");
const qrPopover = $<HTMLDivElement>("#qr-popover");
const qrPopoverClose = $<HTMLButtonElement>("#qr-popover-close");
const qrPopoverCode = $<HTMLDivElement>("#qr-popover-code");
const alongRouteListEl = $<HTMLDivElement>("#along-route-list");

// Place preview card
const placeCard = $<HTMLElement>("#place-card");
const placeLogoEl = $<HTMLImageElement>("#place-logo");
const placePhotoEl = $<HTMLImageElement>("#place-photo");
const placeNameEl = $<HTMLHeadingElement>("#place-name");
const placeCategoryBadge = $<HTMLDivElement>("#place-category-badge");
const placeDetailsEl = $<HTMLDivElement>("#place-details");
const placeRouteToBtn = $<HTMLButtonElement>("#place-route-to-btn");
const placeRouteFromBtn = $<HTMLButtonElement>("#place-route-from-btn");
const placeCloseBtn = $<HTMLButtonElement>("#place-close-btn");

const keyboardHost = $<HTMLDivElement>("#keyboard");

const fields: Record<FieldName, { input: HTMLInputElement; clear: HTMLButtonElement }> = {
  from: { input: $("#from-input"), clear: document.querySelector('.clear-field[data-target="from"]')! },
  to: { input: $("#to-input"), clear: document.querySelector('.clear-field[data-target="to"]')! },
};


venueNameTextEl.textContent = config.venueName;

if (config.kioskLabel) {
  kioskLabelTextEl.textContent = config.kioskLabel;
  kioskLabelBadgeEl.hidden = false;
}

// --- App state ---------------------------------------------------------------

let mapData: MapData;
let mapView: MapView;
let directory: StandEntry[] = [];
/** Emergency exits (doors) — searchable as a destination and marked on the map. See `buildExitDirectory`. */
let exitDirectory: ExitAnchor[] = [];
let activeField: FieldName = "to";
const selection: Record<FieldName, RouteOrigin | null> = { from: null, to: null };
let kioskEntry: RouteOrigin | null = null;
let selectedPlaceEntry: StandEntry | null = null;
let is3DMode = true;
let isCameraLocked = false;
let lockBtnHideTimer: number | undefined;
let activeCategory: string | null = null;
let allVenueSpaces: Space[] = [];
let floors: Floor[] = [];
type SearchTab = "listing" | "keyboard";
let searchTab: SearchTab = "listing";
/** Whether the From/To picker (keyboard + listing) is showing — see `openFieldPicker`. */
let searchPickerOpen = false;
/** Pending auto-start of the route after a list pick — see `scheduleAutoRoute`. */
let autoRouteTimer: number | undefined;
let categoryIcons: Map<string, string> = new Map();

// POI state
let poiDirectory: POIEntry[] = [];

const keyboard = new OnScreenKeyboard(keyboardHost);
const occupancyService = new OccupancyService();

// --- Boot --------------------------------------------------------------------

boot().catch((err) => {
  console.error(err);
  showError(err instanceof Error ? err.message : t("couldNotLoadMap"));
});

// Cache the app shell so the kiosk still boots (including the offline map
// bundle it just fetched) if a refresh happens with no network.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch((err) => console.error("SW registration failed", err));
}

function updateConnectionBadge(): void {
  const isOnline = navigator.onLine;
  connectionBadgeEl.classList.toggle("is-offline", !isOnline);
  connectionTextEl.textContent = isOnline ? t("online") : t("offline");
}

async function boot(): Promise<void> {
  initTheme();
  if (!config.showLanguageSwitcher) langSwitcherEl.hidden = true;
  if (!config.showVenueName) venueNameTextEl.parentElement!.hidden = true;
  if (!config.showClock) clockEl.hidden = true;
  if (!config.showCameraDebug) cameraDebugEl.hidden = true;
  if (!config.showThemeToggle) themeBtn.hidden = true;
  if (!config.showResetButton) homeBtn.hidden = true;
  if (!config.showReloadButton) reloadBtn.hidden = true;
  wireBottomSearch();
  if (config.showConnectionBadge) {
    connectionBadgeEl.hidden = false;
    updateConnectionBadge();
    window.addEventListener("online", updateConnectionBadge);
    window.addEventListener("offline", updateConnectionBadge);
  }
  initLocale();
  langBtnCodeEl.textContent = getLocale().toUpperCase();
  lockBtn.title = t("lockRotation");

  const stopLoaderMessages = startLoaderMessageCycle();

  const { mapData: data, mapView: view } = await initMap(mapContainer);
  mapData = data;
  mapView = view;

  directory = buildDirectory(mapData);
  if (directory.length === 0) {
    showError(t("noExhibitorsYet"));
  }
  categoryIcons = categoryIconMap(mapData);

  // Prefer each stand/category's local image file (public/logos, public/stands,
  // public/category-icons) over Mappedin's own remotely-hosted one — those sit
  // behind a short-lived signed CDN URL that never loads once this kiosk is
  // offline. Awaited before anything below reads iconUrl/photoUrl, so a stand
  // with only a local logo doesn't briefly get both a text label (see
  // addExhibitorLabels) and, once resolved, a colliding logo marker.
  await Promise.all([resolveLocalDirectoryAssets(directory), resolveLocalCategoryIcons(categoryIcons)]);

  allVenueSpaces = mapData.getByType("space");

  // Add exhibitor labels on the 3D map
  addExhibitorLabels(mapView, directory);

  // Float each location's own uploaded logo above its space, where one exists
  renderLocationLogos(mapView, directory);

  // Emergency exits: a red sign on each exit door; tapping one makes it the destination.
  exitDirectory = buildExitDirectory(mapData);
  renderExitMarkers(mapView, exitDirectory, selectExit);
  if (config.showNearestExit && exitDirectory.length > 0) {
    nearestExitBtn.hidden = false;
    nearestExitBtn.addEventListener("click", () => void goToNearestExit());
  }

  // Paint each location's own uploaded photo onto its stand's own floor texture
  // (a `public/stands/<externalId>.jpeg` local "3D case" file wins when present)
  void renderLocationPhotos(mapView, directory);

  // Tint each stand cube's floor by which VITE_STAND_COLOR_RANGES bucket its External ID falls into
  void applyStandColorRanges(mapView, directory);

  // Cache every stand's real floor outline so a tap that lands on a boxed
  // stand's cap/photo/colored wall (all of which sit in front of the space
  // and can swallow the raycast) still resolves to that stand — see
  // `onSpaceClick`'s coordinate fallback.
  setStandHitRings(directory);

  // Identify kiosk position
  if (config.kioskLocationExternalId) {
    kioskEntry = findKioskAnchor(mapData, config.kioskLocationExternalId, directory) ?? null;
  }
  if (!kioskEntry && directory.length > 0) {
    kioskEntry = directory[0];
  }
  if (kioskEntry) {
    addKioskMarker(mapView, routeOriginTarget(kioskEntry));
  }

  // Static "kiosk stand" icons for every physical kiosk across the venue
  // (see VITE_ALL_KIOSK_EXTERNAL_IDS) — shown alongside "You Are Here" on
  // every device, set once here at boot and never touched again (unlike
  // "You Are Here", which re-anchors for shared routes/live GPS).
  const kioskPointerTargets = config.allKioskExternalIds
    .map((id) => findAnchorTarget(mapData, id))
    .filter((target): target is Space | PointOfInterest => Boolean(target));
  if (kioskPointerTargets.length > 0) {
    renderKioskPointerMarkers(mapView, kioskPointerTargets);
  }

  // Same default the idle-reset applies (see resetAll) — pre-fill "From"
  // with this kiosk's own location up front too, so the very first visitor
  // doesn't have to pick "This Kiosk" manually before it's ever gone idle.
  applyDefaultFromSelection();

  // Cache vantage points from live venue geometry for cinematic idle resets.
  // This also bakes in this device's VITE_DEFAULT_CAMERA_* fixed orientation
  // (if set), so the very first paint below already matches it exactly —
  // not just later resets. The default view always centers on wherever the
  // "You Are Here" marker just got placed above (kioskEntry — either the
  // configured VITE_KIOSK_LOCATION_EXTERNAL_ID stand, or the directory[0]
  // fallback when that's not set) instead of the whole venue.
  captureVenuePresets(mapView, mapData, kioskEntry ? routeOriginTarget(kioskEntry) : null, Boolean(kioskEntry));

  // Snap instantly (no animation) to the same "default-view" preset that
  // resets fall back to, so boot and reset always agree on the default view
  // instead of drifting apart.
  applyCameraPreset(mapView, mapData, "default-view", 0);

  // On mobile the map always boots unlocked — rotation happens via touch
  // gestures there, so the auto-lock (VITE_DEFAULT_CAMERA_LOCK) only applies
  // to the kiosk/desktop layouts.
  if (config.defaultCameraLock && !isMobileViewport()) {
    isCameraLocked = true;
    setCameraRotationLock(mapView, true);
    applyLockUiState();
  }

  // Simulated live occupancy per stand (see src/occupancy.ts)
  occupancyService.start(directory.map((e) => e.space.id));

  enableInteractivity(mapView, mapData);

  // Tapping a space opens its place card and glides the camera to it; tapping
  // the already-selected stand again unfocuses it — closes the card and
  // zooms the camera out (keeping the current framing/center). Only stands
  // with an actual name respond — unnamed polygons do nothing on tap.
  onSpaceClick(mapView, (space) => {
    const entry = directory.find((e) => e.space.id === space.id);
    if (!entry || !entry.name.trim()) return;
    if (selectedPlaceEntry?.id === entry.id) {
      closePlaceCard();
      void mapView.Camera.animateTo(
        { zoomLevel: Math.min(mapView.Camera.maxZoomLevel, 19.5) },
        { duration: 700, easing: "ease-in-out" },
      );
      return;
    }
    openPlaceCard(entry);
    void mapView.Camera.focusOn([space], { minZoomLevel: 20, maxZoomLevel: 20, duration: 700 });
  });

  floors = getFloors(mapData);

  renderCategoryChips();
  poiDirectory = buildPOIDirectory(mapData);
  renderFloorPicker();
  renderLangMenu();

  onFloorChange(mapView, (floor) => setActiveFloorPill(floor.id));

  // Live zoom/bearing/pitch readout in the header — lets an operator read
  // off the exact numbers to copy into VITE_DEFAULT_CAMERA_* in .env while
  // manually framing the kiosk's fixed default view.
  updateCameraDebugReadout(mapView.Camera);
  mapView.on("camera-change", () => updateCameraDebugReadout(mapView.Camera));

  const idle = new IdleManager(
    idleOverlay,
    config.idleTimeoutMs,
    () => {
      resetAll();
      showIdleVideo();
    },
    hideIdleVideo,
  );
  // A hall QR link (?hall=…) is opened on a visitor's own phone, so the
  // kiosk idle reset would only snap their view back to the default.
  // Same for any mobile-width screen: the idle reset is for the shared
  // physical kiosk, and would just snap a phone's view back mid-use.
  if (!isMobileViewport() && !new URLSearchParams(window.location.search).get("hall")) idle.start();

  startClock();
  wireInputs();
  wireControls();
  wireSingleTouchUi();
  wireLanguageSwitcher();
  wireHeaderMenu();
  startCompassSync();
  restoreSharedRoute();
  restoreHallLink();

  // Fade out and remove the dark loading veil now that the map canvas is ready
  stopLoaderMessages();
  mapLoader.classList.add("hidden");
  mapLoader.addEventListener("transitionend", () => mapLoader.remove(), { once: true });
}

/**
 * Cycles the loading veil's caption through a sequence of venue-prep
 * flavor lines instead of one static "Loading venue map…" the whole time —
 * boot can take a few seconds (fetching/hydrating the offline MVF bundle,
 * initializing WebGL, resolving local logos/photos), and rotating captions
 * read as active progress instead of a stall. Purely cosmetic — nothing
 * here is tied to boot's actual real progress. Returns a function that
 * stops the rotation; call it once the loader is about to be hidden.
 */
function startLoaderMessageCycle(): () => void {
  const keys: (keyof Dictionary)[] = [
    "loadingVenueMap",
    "loadingCalibratingFloors",
    "loadingPlacingStands",
    "loadingMappingRoutes",
    "loadingPolishing3D",
    "loadingOptimizingTour",
    "loadingAlmostThere",
  ];
  let i = 0;
  const intervalId = window.setInterval(() => {
    i = (i + 1) % keys.length;
    mapLoaderTextEl.textContent = t(keys[i]);
  }, 1900);
  return () => window.clearInterval(intervalId);
}

/**
 * Reflects live GPS tracking status (see `startLiveLocationTracking`) in the
 * header badge — "Locating…" while waiting for the first fix, "Live · ±Nm"
 * once positioned, or a plain-language reason it can't work (permission
 * denied / no GPS signal) so a visitor understands why "You Are Here" isn't
 * moving instead of assuming the app is broken.
 */
function updateLiveLocationBadge(state: "acquiring" | "active" | LiveLocationErrorKind, accuracyMeters?: number): void {
  liveLocationBadgeEl.hidden = false;
  liveLocationBadgeEl.classList.toggle("is-active", state === "active");
  liveLocationBadgeEl.classList.toggle("is-error", state === "denied" || state === "unavailable");
  liveLocationTextEl.textContent =
    state === "active"
      ? t("liveLocationActive", { n: accuracyMeters ?? 0 })
      : state === "acquiring"
        ? t("liveLocationAcquiring")
        : state === "denied"
          ? t("liveLocationDenied")
          : t("liveLocationUnavailable");
}

/**
 * Reproduces a route shared via QR code (see `openQrPopover`/`buildShareUrl`):
 * a `?from=<spaceId>&to=<spaceId>` in the URL selects those two stands and
 * immediately calculates the route, so scanning the code on a visitor's own
 * phone shows the exact same directions they were already looking at.
 */
function restoreSharedRoute(): void {
  const params = new URLSearchParams(window.location.search);
  const fromId = params.get("from");




  const kioskId = params.get("kiosk");
  const toId = params.get("to");
  if ((!fromId && !kioskId) || !toId) return;

  // `from` is a stand's Space id; `kiosk` is a kiosk anchor's External ID
  // (see buildShareUrl) — the physical device the QR was scanned from.
  const fromEntry: RouteOrigin | undefined = fromId
    ? directory.find((e) => e.space.id === fromId)
    : findKioskAnchor(mapData, kioskId!, directory);
  const toEntry = directory.find((e) => e.space.id === toId);
  if (!fromEntry || !toEntry) return;

  // On the kiosk itself, "You Are Here" marks the fixed physical device
  // location (VITE_KIOSK_LOCATION_EXTERNAL_ID). A visitor's own phone has
  // no such fixed spot, so the shared route's From stand takes over that
  // role instead — it's wherever they were standing when they scanned it.
  kioskEntry = fromEntry;
  addKioskMarker(mapView, routeOriginTarget(fromEntry));

  selection.from = fromEntry;
  fields.from.input.value = fromEntry.name;
  fields.from.clear.hidden = false;
  selectEntry("to", toEntry);
  void calculateAndShowRoute({ anchorKioskMarkerToRouteStart: true });

  // This page is now running on a visitor's own phone (they got here by
  // scanning the QR code), not the fixed physical kiosk — so "You Are Here"
  // should track their real, live position instead of staying pinned to the
  // shared route's static From stand. Keeps updating even after they pick a
  // new destination, since it reflects where they actually are, not the route.
  updateLiveLocationBadge("acquiring");
  startLiveLocationTracking(mapView, {
    onFix: (accuracyMeters) => updateLiveLocationBadge("active", accuracyMeters),
    onError: (kind) => updateLiveLocationBadge(kind),
  });
}

/**
 * Opens the map centered on one hall, from a printed hall QR code: a
 * `?hall=Hall 3` in the URL (matched by location name, case-insensitive)
 * frames that hall's space — the camera's focus transform centers on the
 * space's exact bounds. Meant for visitors' phones; ignored when the link
 * is a shared route (`?from=&to=`), which takes over the view itself.
 */
function restoreHallLink(): void {
  const params = new URLSearchParams(window.location.search);
  // Accepts "hall-3" / "hall_3" / "Hall 3" / bare "3" — dashes and
  // underscores stand in for the space so printed links need no %20.
  let hallName = params.get("hall")?.trim().toLowerCase().replace(/[-_\s]+/g, " ");
  if (hallName && /^\d+$/.test(hallName)) hallName = `hall ${hallName}`;
  if (!hallName || (params.get("from") && params.get("to"))) return;

  const entry = directory.find((e) => e.name.trim().toLowerCase().replace(/\s+/g, " ") === hallName);
  if (!entry) {
    console.warn(`Hall link: no location named "${params.get("hall")}"`);
    return;
  }

  const target = mapView.Camera.getFocusOnTransform(entry.space, {
    pitch: config.defaultCameraPitch ?? 55,
    minZoomLevel: 17,
  });
  if (config.defaultCameraBearing !== null) target.bearing = config.defaultCameraBearing;
  void mapView.Camera.animateTo(target, { duration: 900, easing: "ease-in-out" });
}

// Removed: onStartTap — map is immediately live on boot.

// --- Theme Engine ------------------------------------------------------------

function initTheme(): void {
  const saved = localStorage.getItem("mappedin-theme") || "dark";
  document.documentElement.setAttribute("data-theme", saved);
  document.documentElement.style.setProperty("--logo-padding", `${config.logoPadding}px`);

  themeBtn.addEventListener("click", () => {
    const current = document.documentElement.getAttribute("data-theme");
    const next = current === "light" ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("mappedin-theme", next);

    if (mapView && mapData) {
      updateHoverColors(mapView, mapData);
    }
  });
}

// --- Real-time Compass & Camera Navigation Controls --------------------------

function wireControls(): void {
  // Reset North
  compassBtn.addEventListener("click", () => {
    resetNorth(mapView);
  });

  // 2D / 3D Tilt perspective toggle — stays usable even while rotation is
  // locked; only the bearing itself is kept frozen in that case.
  tiltBtn.addEventListener("click", () => {
    is3DMode = !is3DMode;
    tiltLabel.textContent = is3DMode ? "2D" : "3D";
    tiltBtn.classList.toggle("active", is3DMode);
    setPitchMode(mapView, is3DMode ? "3d" : "2d", isCameraLocked);
  });

  // The route intro plays flat and tilts back to 3D on arrival — keep the toggle matching the camera.
  onRouteIntroPitchChange((mode) => {
    is3DMode = mode === "3d";
    tiltLabel.textContent = is3DMode ? "2D" : "3D";
    tiltBtn.classList.toggle("active", is3DMode);
  });

  // Rotation Left / Right
  wireRotateButton(rotateLeftBtn, -45);
  wireRotateButton(rotateRightBtn, 45);

  // Rotation Lock — an operator-only control, hidden from regular visitors.
  // Long-press its (invisible) spot in the dock to reveal it; it hides
  // itself again after 10s of not being touched, or immediately on Reset.
  wireLongPress(lockBtn, () => revealLockBtn());

  lockBtn.addEventListener("click", () => {
    // A quick tap while hidden shouldn't do anything — only a revealed
    // (long-pressed) button responds to taps.
    if (!lockBtn.classList.contains("revealed")) return;

    isCameraLocked = !isCameraLocked;
    setCameraRotationLock(mapView, isCameraLocked);

    // Remember exactly this pose — pan/zoom stay usable while locked, so a
    // visitor can drift away from it, and reset should bring it right back.
    if (isCameraLocked) {
      captureCurrentCameraAsPreset(mapView, "locked-view");
    }

    applyLockUiState();

    // Give the operator a fresh 10s window after each use, instead of
    // hiding right under their thumb mid-interaction.
    revealLockBtn();
  });

  // Zoom In / Out
  zoomInBtn.addEventListener("click", () => {
    zoomCamera(mapView, 0.8);
  });

  zoomOutBtn.addEventListener("click", () => {
    zoomCamera(mapView, -0.8);
  });

  // Re-center venue
  recenterBtn.addEventListener("click", () => {
    focusWholeVenue(mapView, mapData);
  });

  // Start Over / Reset
  homeBtn.addEventListener("click", resetAll);

  // Place card buttons
  placeCloseBtn.addEventListener("click", closePlaceCard);

  placeRouteToBtn.addEventListener("click", () => {
    if (!selectedPlaceEntry) return;
    selectEntry("to", selectedPlaceEntry);
    closePlaceCard();
    void calculateAndShowRoute();
  });

  placeRouteFromBtn.addEventListener("click", () => {
    if (!selectedPlaceEntry) return;
    selectEntry("from", selectedPlaceEntry);
    closePlaceCard();
  });

  swapBtn.addEventListener("click", swapFields);
  clearRouteBtn.addEventListener("click", () => {
    clearRoute(mapView);
    resultSection.hidden = true;
    shareRouteBtn.hidden = true;
    closeQrPopover();
    alongRouteSection.hidden = true;
    alongRouteListEl.innerHTML = "";
    // Nothing left to show on a mobile phone once the route is cleared —
    // reopen the picker so there's still a way to choose a new one.
    setPickerCollapsed(false);
  });

  shareRouteBtn.addEventListener("click", openQrPopover);
  qrPopoverClose.addEventListener("click", closeQrPopover);

  mobilePickerToggleBtn.addEventListener("click", () => {
    setPickerCollapsed(!panelEl.classList.contains("picker-collapsed"));
  });

  wireDrawerHandleSwipe(drawerHandleEl);

  stepsToggleBtn.addEventListener("click", () => {
    setStepsExpanded(!resultSection.classList.contains("steps-expanded"));
  });
}

/**
 * Expands/collapses the turn-by-turn step list — kiosk desktop-only; the
 * toggle and list are hidden entirely on mobile (see the mobile media
 * query in style.css).
 */
function setStepsExpanded(expanded: boolean): void {
  resultSection.classList.toggle("steps-expanded", expanded);
  stepsToggleBtn.setAttribute("aria-expanded", String(expanded));
  stepsToggleBtn.querySelector("span")!.textContent = expanded ? t("hideSteps") : t("showSteps");
}

/**
 * Shows/hides the From/To picker (category chips, search row, routing
 * options) vs. the directions result — only visually meaningful on a
 * phone-width screen (see the mobile media query in style.css); the
 * kiosk's own desktop layout ignores this class entirely and always shows
 * the full picker.
 *
 * Also syncs the "picker-open" active class on the left-corner grid button
 * so its icon highlights blue while the drawer is expanded.
 */
function setPickerCollapsed(collapsed: boolean): void {
  panelEl.classList.toggle("picker-collapsed", collapsed);
  // "picker-open" = NOT collapsed = drawer is expanded
  mobilePickerToggleBtn.classList.toggle("picker-open", !collapsed);
}

/**
 * Swiping the drawer handle up expands the picker (From/To grid), swiping
 * it down collapses it back to just the logo header — same
 * `setPickerCollapsed()` call the mobile grid button itself uses.
 */
function wireDrawerHandleSwipe(el: HTMLElement): void {
  const SWIPE_THRESHOLD_PX = 24;
  let startY = 0;

  el.addEventListener("pointerdown", (e) => {
    startY = e.clientY;
    el.setPointerCapture(e.pointerId);
  });

  el.addEventListener("pointerup", (e) => {
    const deltaY = e.clientY - startY;
    if (deltaY <= -SWIPE_THRESHOLD_PX) {
      setPickerCollapsed(false);
    } else if (deltaY >= SWIPE_THRESHOLD_PX) {
      setPickerCollapsed(true);
    }
  });
}

/**
 * A route can be shared when it ends at a searchable stand and starts at
 * either another stand or this kiosk's own configured spot (a Point of
 * Interest rather than a stand — see `findKioskAnchor`). A live-GPS start
 * is never shareable: it only exists on one phone.
 */
function isShareableRoute(from: RouteOrigin, to: RouteOrigin): boolean {
  if (!isStandEntry(to)) return false;
  if (isStandEntry(from)) return true;
  return !isLiveLocationAnchor(from) && Boolean(config.kioskLocationExternalId) && from.id === kioskEntry?.id;
}

/**
 * Builds a URL back to this same kiosk app with the current From/To stands
 * encoded as query params, so opening it (via QR scan) reproduces the exact
 * same route on another device. Points at `config.shareBaseUrl` (the kiosk's
 * real public domain) rather than `window.location.origin`, which resolves
 * to `localhost` or a bare LAN IP while developing — an address no visitor's
 * phone could ever actually reach.
 */
function buildShareUrl(from: RouteOrigin, to: StandEntry): string {
  const params = new URLSearchParams();
  // Carry the img/box mode over to the visitor's phone (first, so the link
  // reads ?mode=box&from=…&to=…).
  const modeParam = new URLSearchParams(window.location.search).get("mode");
  if (modeParam) params.set("mode", modeParam);
  // A stand is shared by its Space id; this kiosk's own anchor (a Point of
  // Interest, not a stand) by its External ID, resolved again on the phone.
  if (isStandEntry(from)) params.set("from", from.space.id);
  else params.set("kiosk", config.kioskLocationExternalId!);
  params.set("to", to.space.id);
  return `${config.shareBaseUrl}${window.location.pathname}?${params.toString()}`;
}

/**
 * Positions the QR popover relative to the From/To grid ("search-row")
 * instead of leaving it in normal document flow — it lives outside #panel
 * (see index.html) specifically so it isn't clipped by the panel's own
 * scroll/overflow, which means its position has to be set here in JS.
 */
function positionQrPopover(): void {
  const margin = 12;
  const popoverWidth = 220;
  const isMobileDrawer = window.matchMedia("(max-width: 639px)").matches;
  const searchRect = searchRowEl.getBoundingClientRect();
  const panelRect = panelEl.getBoundingClientRect();

  if (isMobileDrawer) {
    // #panel becomes a full-width bottom drawer at this size — float the
    // popover just above it instead of off to a side that doesn't exist.
    qrPopover.style.width = "auto";
    qrPopover.style.left = `${margin}px`;
    qrPopover.style.right = `${margin}px`;
    qrPopover.style.top = "auto";
    qrPopover.style.bottom = `${window.innerHeight - panelRect.top + margin}px`;
  } else {
    // Kiosk/desktop layout — float just outside the panel, top-aligned
    // with the From/To grid it's sharing.
    qrPopover.style.width = `${popoverWidth}px`;
    qrPopover.style.right = "auto";
    qrPopover.style.bottom = "auto";
    qrPopover.style.left = `${Math.min(panelRect.right + margin, window.innerWidth - popoverWidth - margin)}px`;
    qrPopover.style.top = `${searchRect.top}px`;
  }
}

async function openQrPopover(): Promise<void> {
  // A shared link encodes Space ids that the recipient's phone resolves back
  // through the directory (see restoreSharedRoute) — that only works when
  // both ends are full stand entries, not a bare kiosk Point-of-Interest
  // anchor (see findKioskAnchor), so sharing is a no-op in that case. The
  // share button is already hidden whenever this would apply (renderResult).
  if (!selection.from || !selection.to || !isShareableRoute(selection.from, selection.to)) return;
  const url = buildShareUrl(selection.from, selection.to as StandEntry);

  // Loaded on demand instead of bundled up front — the QR encoder is only
  // ever needed if someone actually taps Share, which most visits never do.
  const { default: qrcode } = await import("qrcode-generator");
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  qrPopoverCode.innerHTML = qr.createSvgTag(6, 8);

  positionQrPopover();
  qrPopover.hidden = false;
}

function closeQrPopover(): void {
  qrPopover.hidden = true;
  qrPopoverCode.innerHTML = "";
}

/**
 * A tap rotates by `fullStepDegrees` (e.g. 45°); holding the button down
 * past `LONG_PRESS_MS` instead rotates by half that step, for fine-tuning
 * alignment when the venue's natural bearing isn't a clean multiple of 45°.
 * The long press suppresses the trailing `click` so it doesn't also fire
 * the full-step rotation.
 */
function wireRotateButton(btn: HTMLButtonElement, fullStepDegrees: number): void {
  const LONG_PRESS_MS = 450;
  const halfStepDegrees = fullStepDegrees / 2;
  let pressTimer: number | undefined;
  let longPressFired = false;

  const clearPressTimer = () => {
    if (pressTimer !== undefined) {
      window.clearTimeout(pressTimer);
      pressTimer = undefined;
    }
  };

  btn.addEventListener("pointerdown", () => {
    longPressFired = false;
    clearPressTimer();
    pressTimer = window.setTimeout(() => {
      longPressFired = true;
      rotateCamera(mapView, halfStepDegrees);
    }, LONG_PRESS_MS);
  });

  btn.addEventListener("pointerup", clearPressTimer);
  btn.addEventListener("pointerleave", clearPressTimer);
  btn.addEventListener("pointercancel", clearPressTimer);

  btn.addEventListener("click", () => {
    if (longPressFired) {
      longPressFired = false;
      return;
    }
    rotateCamera(mapView, fullStepDegrees);
  });
}

// --- Multi-Floor Level Switcher Dock ------------------------------------------

function renderFloorPicker(): void {
  floorPickerEl.innerHTML = "";

  if (floors.length <= 1) {
    floorPickerEl.hidden = true;
    return;
  }
  floorPickerEl.hidden = false;

  // Highest floor first, matching a physical elevator panel.
  for (const floor of [...floors].reverse()) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "floor-pill";
    btn.dataset.floorId = floor.id;
    btn.textContent = floor.shortName || floor.name;
    btn.title = floor.name;
    btn.setAttribute("aria-label", floor.name);
    btn.classList.toggle("active", mapView.currentFloor?.id === floor.id);
    btn.addEventListener("click", () => {
      void switchFloor(mapView, floor.id);
    });
    floorPickerEl.appendChild(btn);
  }
}

function setActiveFloorPill(floorId: string): void {
  floorPickerEl.querySelectorAll<HTMLButtonElement>(".floor-pill").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.floorId === floorId);
  });
}

// --- Language Switcher ---------------------------------------------------------

function renderLangMenu(): void {
  langMenuEl.innerHTML = "";
  for (const info of SUPPORTED_LOCALES) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lang-option";
    btn.classList.toggle("active", info.code === getLocale());
    btn.textContent = info.nativeLabel;
    btn.dir = info.rtl ? "rtl" : "ltr";
    btn.addEventListener("click", () => {
      applyLocale(info.code);
      closeLangMenu();
    });
    langMenuEl.appendChild(btn);
  }
}

function openLangMenu(): void {
  langMenuEl.hidden = false;
  langBtn.setAttribute("aria-expanded", "true");
}

function closeLangMenu(): void {
  langMenuEl.hidden = true;
  langBtn.setAttribute("aria-expanded", "false");
}

function applyLocale(locale: Locale): void {
  setLocale(locale);
  langBtnCodeEl.textContent = locale.toUpperCase();
  lockBtn.title = isCameraLocked ? t("unlockRotation") : t("lockRotation");
  renderLangMenu();
  renderCategoryChips();
  poiDirectory = buildPOIDirectory(mapData);
  if (selectedPlaceEntry) openPlaceCard(selectedPlaceEntry);

  // Best-effort: only affects venues with multilingual location data
  // configured in Mappedin admin. No-op (rejects quietly) otherwise.
  void mapData?.changeLanguage(locale).catch(() => { });
}

function wireLanguageSwitcher(): void {
  langBtn.addEventListener("click", () => {
    const expanded = langBtn.getAttribute("aria-expanded") === "true";
    if (expanded) closeLangMenu();
    else openLangMenu();
  });

  document.addEventListener("pointerdown", (e) => {
    if (!langSwitcherEl.contains(e.target as Node)) closeLangMenu();
  });
}

/**
 * Mobile-only header overflow menu (clock, connection badge, camera debug,
 * language switcher, theme toggle, reset) — see "display: contents" on
 * #header-menu in style.css, which makes this a no-op on the kiosk's own
 * desktop layout where those items already show directly in the header.
 */
/**
 * On mobile the header items live behind the hamburger. With only one of
 * them showing (the others switched off via VITE_SHOW_*, or just not
 * applicable like the live-location badge), a menu holding a single row is
 * pointless — so `menu-single` shows that item directly in the header and
 * hides the hamburger, and `menu-empty` hides the hamburger when nothing
 * is left. Re-evaluated whenever an item's `hidden` flips. No effect on the
 * desktop/kiosk layout (the rules live in the mobile media query).
 */
function updateHeaderMenuMode(): void {
  const menuEl = document.getElementById("header-menu")!;
  const visible = Array.from(menuEl.children).filter(
    (el) => !(el as HTMLElement).hidden && !(el.id === "camera-debug" && !el.textContent),
  ).length;
  headerEl.classList.toggle("menu-single", visible === 1);
  headerEl.classList.toggle("menu-empty", visible === 0);
  if (visible <= 1) {
    headerEl.classList.remove("menu-open");
    headerMenuBtn.setAttribute("aria-expanded", "false");
  }
}

function wireHeaderMenu(): void {
  new MutationObserver(updateHeaderMenuMode).observe(document.getElementById("header-menu")!, {
    attributes: true,
    attributeFilter: ["hidden"],
    subtree: true,
  });
  updateHeaderMenuMode();

  headerMenuBtn.addEventListener("click", () => {
    const isOpen = headerEl.classList.toggle("menu-open");
    headerMenuBtn.setAttribute("aria-expanded", String(isOpen));
  });

  document.addEventListener("pointerdown", (e) => {
    if (!headerEl.contains(e.target as Node)) {
      headerEl.classList.remove("menu-open");
      headerMenuBtn.setAttribute("aria-expanded", "false");
    }
  });
}

/**
 * Kiosk case (a URL with no from/to — not a shared route): everything except
 * the map — header, search panel, suggestions, place card, buttons — is
 * one-finger only. The map keeps its own one- and two-finger gestures, but a
 * pinch that STARTS on the UI would otherwise zoom the whole page (making
 * the screen bigger) in browsers that ignore the viewport's user-scalable=no
 * (Windows touchscreens, iOS Safari). Two layers: `touch-action` in CSS
 * (see `single-touch-ui` in style.css) and, for engines that still pinch,
 * a non-passive guard that cancels any multi-touch gesture that began
 * outside #map. Scrolling the lists with one finger is untouched.
 */
function wireSingleTouchUi(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get("from") && params.get("to")) return;

  document.documentElement.classList.add("single-touch-ui");
  const mapEl = document.getElementById("map")!;

  document.addEventListener(
    "touchstart",
    (e) => {
      // A second finger landing while any finger is on the UI = pinch on the UI.
      if (e.touches.length > 1 && Array.from(e.touches).some((t) => !mapEl.contains(t.target as Node))) {
        e.preventDefault();
      }
    },
    { passive: false },
  );
  document.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches.length > 1 && Array.from(e.touches).some((t) => !mapEl.contains(t.target as Node))) {
        e.preventDefault();
      }
    },
    { passive: false },
  );
  // Safari's own pinch events (it fires these regardless of user-scalable).
  for (const type of ["gesturestart", "gesturechange", "gestureend"]) {
    document.addEventListener(
      type,
      (e) => {
        if (!mapEl.contains(e.target as Node)) e.preventDefault();
      },
      { passive: false },
    );
  }
}

function startCompassSync(): void {
  let lastBearing = -1;
  const tick = () => {
    if (mapView?.Camera) {
      const bearing = mapView.Camera.bearing;
      if (Math.abs(bearing - lastBearing) > 0.1) {
        lastBearing = bearing;
        compassNeedle.style.transform = `rotate(${-bearing}deg)`;
      }
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// --- Stand Inspector / Place Card --------------------------------------------

/**
 * Hides `img` instead of leaving the browser's default broken-image glyph
 * showing — for when even the resolved URL (local file or, offline, the
 * remote Mappedin CDN fallback — see `resolveLocalDirectoryAssets` in
 * directory.ts) fails to load. `{ once: true }` since a fresh handler is
 * wired on every `openPlaceCard` call anyway.
 */
function hideImageOnError(img: HTMLImageElement): void {
  img.addEventListener(
    "error",
    () => {
      img.hidden = true;
      img.removeAttribute("src");
    },
    { once: true },
  );
}

function openPlaceCard(entry: StandEntry): void {
  selectedPlaceEntry = entry;
  placeNameEl.textContent = entry.name;
  placeCategoryBadge.textContent = entry.categoryNames[0] || t("exhibitor");

  if (entry.iconUrl) {
    hideImageOnError(placeLogoEl);
    placeLogoEl.src = entry.iconUrl;
    placeLogoEl.alt = entry.name;
    placeLogoEl.hidden = false;
  } else {
    placeLogoEl.hidden = true;
    placeLogoEl.removeAttribute("src");
  }

  if (entry.photoUrl) {
    hideImageOnError(placePhotoEl);
    placePhotoEl.src = entry.photoUrl;
    placePhotoEl.alt = entry.name;
    placePhotoEl.hidden = false;
  } else {
    placePhotoEl.hidden = true;
    placePhotoEl.removeAttribute("src");
  }

  placeDetailsEl.innerHTML = "";
  if (entry.categoryNames.length > 1) {
    const meta = document.createElement("p");
    meta.className = "place-meta-tags";
    meta.textContent = entry.categoryNames.slice(1).join(" · ");
    placeDetailsEl.appendChild(meta);
  }

  const extId = entry.location.externalId || entry.space.externalId;
  if (extId) {
    const extEl = document.createElement("div");
    extEl.className = "place-id-text";
    extEl.textContent = `Stand No.${extId}`;
    placeDetailsEl.appendChild(extEl);
  }

  placeCard.hidden = false;
}

function occupancyLabel(level: OccupancyLevel): string {
  switch (level) {
    case "low":
      return t("occupancyLow");
    case "moderate":
      return t("occupancyModerate");
    case "busy":
      return t("occupancyBusy");
  }
}

function closePlaceCard(): void {
  placeCard.hidden = true;
  selectedPlaceEntry = null;
}

// --- Category chips ------------------------------------------------------------

function renderCategoryChips(): void {
  categoryChipsEl.innerHTML = "";
  const categories = allCategories(directory);
  if (categories.length === 0) return;

  // "All" chip
  const allChip = document.createElement("button");
  allChip.type = "button";
  allChip.className = "chip";
  allChip.classList.toggle("active", activeCategory === null);
  allChip.textContent = t("allStands");
  allChip.addEventListener("click", () => {
    setActiveChip(allChip);
    activeCategory = null;
    highlightZone(mapView, allVenueSpaces, null);
    showCategoryListing();
  });
  categoryChipsEl.appendChild(allChip);

  for (const name of categories) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip";
    chip.classList.toggle("active", activeCategory === name);

    const iconUrl = categoryIcons.get(name);
    if (iconUrl) {
      const icon = document.createElement("img");
      icon.className = "chip-icon";
      icon.src = iconUrl;
      icon.alt = "";
      chip.appendChild(icon);
    }
    chip.appendChild(document.createTextNode(name));

    chip.addEventListener("click", () => {
      setActiveChip(chip);
      activeCategory = name;
      showCategoryListing();

      const matching = byCategory(directory, name);
      const matchingSpaces = matching.map((e) => e.space);
      highlightZone(mapView, allVenueSpaces, matchingSpaces, zoneColorForCategory(name));
      if (matchingSpaces.length > 0) {
        void mapView.Camera.focusOn(matchingSpaces, { duration: 600 });
      }
    });
    categoryChipsEl.appendChild(chip);
  }
}

/**
 * Shows the chosen category's list (nearest first — see `onFieldInput`) in
 * the same place a field tap does: with the picker open, the list floats
 * beside the panel next to the keyboard. Opening it here too matters on a
 * fresh page, where no field has been tapped yet — the list otherwise
 * rendered inside the panel, under the search fields.
 */
function showCategoryListing(): void {
  fields[activeField].input.value = "";
  if (searchPickerOpen) {
    onFieldInput(activeField);
    return;
  }
  fields[activeField].input.focus(); // its focus handler opens the picker
  if (!searchPickerOpen) openFieldPicker(activeField);
}

function setActiveChip(active: HTMLElement): void {
  categoryChipsEl.querySelectorAll(".chip").forEach((c) => c.classList.remove("active"));
  active.classList.add("active");
}

// --- Search fields ---------------------------------------------------------

/** Matches the `max-width: 639px` breakpoint in style.css that switches to the mobile bottom-sheet layout. */
function isMobileViewport(): boolean {
  return window.innerWidth < 640;
}

/**
 * On mobile, visitors already have their own device keyboard, so the
 * bespoke on-screen `OnScreenKeyboard` (built for kiosk/tablet hardware
 * with no physical keyboard) is redundant and just eats screen space the
 * listing needs. `inputmode="none"` on the From/To fields (index.html) is
 * what suppresses the OS keyboard in favor of that on-screen one — this
 * clears it on mobile so focusing a field pops the visitor's own keyboard
 * instead, and keeps it on tablet/kiosk where there's no other keyboard.
 */
function syncMobileKeyboardMode(): void {
  const mobile = isMobileViewport();
  for (const name of ["from", "to"] as const) {
    const { input } = fields[name];
    if (mobile) {
      input.removeAttribute("inputmode");
    } else {
      input.setAttribute("inputmode", "none");
    }
  }
}

function wireInputs(): void {
  syncMobileKeyboardMode();
  window.addEventListener("resize", syncMobileKeyboardMode);

  for (const name of ["from", "to"] as const) {
    const { input, clear } = fields[name];

    input.addEventListener("focus", () => openFieldPicker(name));
    // A tap on a field that still has focus fires no `focus` event — e.g.
    // after a tap on the map closed the picker without taking focus away
    // (the map swallows it). Reopen on the tap itself in that case.
    input.addEventListener("click", () => {
      if (!searchPickerOpen) openFieldPicker(name);
    });

    input.addEventListener("input", () => onFieldInput(name));

    clear.addEventListener("click", () => {
      input.value = "";
      selection[name] = null;
      clear.hidden = true;
      updateGoButton();
      onFieldInput(name);
      input.focus();
    });
  }

  tabListingBtn.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    setSearchTab("listing");
  });
  tabKeyboardBtn.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    setSearchTab("keyboard");
  });

  // On the kiosk/tablet the keyboard + listing stay up when the visitor taps
  // somewhere else (the map, a button) — they close on picking an entry,
  // Get Directions, or Reset. Only on a phone does an outside tap dismiss
  // the in-panel listing, as phone users expect.
  document.addEventListener("pointerdown", (e) => {
    if (!isMobileViewport()) return;
    const target = e.target as Node;
    const withinSearch =
      suggestionsEl.contains(target) ||
      keyboardHost.contains(target) ||
      searchTabsEl.contains(target) ||
      Object.values(fields).some((f) => f.input.contains(target));
    if (!withinSearch) closeSearchPicker();
  });
}

/**
 * Opens the picker for the field that just gained focus. On kiosk/tablet
 * the on-screen keyboard always shows inside the panel, with the listing
 * in its own floating box beside it (#suggestions-flyout) — no
 * Listing/Keyboard switch, so the tab bar stays hidden. Mobile uses the
 * device's own keyboard (see `syncMobileKeyboardMode`), so the listing
 * stays inside the panel there.
 */
function openFieldPicker(name: FieldName): void {
  activeField = name;
  openSearchPicker();
  if (searchTab === "keyboard") positionSuggestionsFlyout();
  onFieldInput(name);
}

function openSearchPicker(): void {
  searchPickerOpen = true;
  syncBottomSearch();
  searchTabsEl.hidden = true;
  searchTab = isMobileViewport() ? "listing" : "keyboard";
  applySearchTab();
}

function closeSearchPicker(): void {
  searchPickerOpen = false;
  syncBottomSearch();
  suggestionsEl.hidden = true;
  searchTabsEl.hidden = true;
  keyboard.hide();
  // Drop focus too, so the next tap on the field is a real focus again.
  const focused = document.activeElement;
  if (focused === fields.from.input || focused === fields.to.input) (focused as HTMLInputElement).blur();
}

function setSearchTab(tab: SearchTab): void {
  searchTab = tab;
  applySearchTab();
}

window.addEventListener("resize", () => {
  if (searchTab === "keyboard") positionSuggestionsFlyout();
});

/**
 * Aligns the floating suggestions flyout with the panel's right edge, at
 * the same vertical position as the keyboard, so it reads as "next to" the
 * panel rather than stacked inside it. Re-run on resize since the panel's
 * position/width changes across the kiosk/tablet/mobile breakpoints.
 */
function positionSuggestionsFlyout(): void {
  const panelRect = panelEl.getBoundingClientRect();
  const fieldRect = fields[activeField].input.getBoundingClientRect();
  suggestionsFlyoutEl.style.left = `${panelRect.right + 12}px`;
  suggestionsFlyoutEl.style.top = `${fieldRect.top}px`;
}

function applySearchTab(): void {
  searchTabsEl.classList.toggle("tab-keyboard-active", searchTab === "keyboard");
  tabListingBtn.classList.toggle("active", searchTab === "listing");
  tabListingBtn.setAttribute("aria-selected", String(searchTab === "listing"));
  tabKeyboardBtn.classList.toggle("active", searchTab === "keyboard");
  tabKeyboardBtn.setAttribute("aria-selected", String(searchTab === "keyboard"));

  if (searchTab === "keyboard") {
    suggestionsFlyoutEl.appendChild(suggestionsEl);
    positionSuggestionsFlyout();
    keyboard.attach(fields[activeField].input, () => onFieldInput(activeField));
  } else {
    keyboardAreaEl.insertBefore(suggestionsEl, keyboardHost);
    keyboard.hide();
  }
}

function onFieldInput(name: FieldName): void {
  cancelAutoRoute(); // back in a field — they're still choosing
  const term = fields[name].input.value;
  fields[name].clear.hidden = term.length === 0;
  if (selection[name] && !showsOrigin(selection[name]!, term)) selection[name] = null;
  // Typed out in full but never tapped in the list: still a valid choice.
  if (!selection[name]) selection[name] = exactEntryMatch(name, term);
  updateGoButton();

  // Typing overrides the active route: clear any displayed directions/listings
  // right away so a new From/To choice starts fresh, without waiting for the
  // separate Clear Route button.
  if (!resultSection.hidden) {
    clearRoute(mapView);
    resultSection.hidden = true;
    shareRouteBtn.hidden = true;
    closeQrPopover();
    alongRouteSection.hidden = true;
    alongRouteListEl.innerHTML = "";
  }

  // No search term yet: show a full, tappable listing (respecting the active
  // category filter, if any) so the field offers a scroll-and-tap list *and*
  // the keyboard at once — pick whichever suits, or type to narrow the list.
  if (term) {
    renderSuggestionList(searchDirectory(directory, term), 8, name, name === "to" ? searchExits(exitDirectory, term) : []);
  } else {
    // Unassigned stands show up with just their stand number as the name
    // (e.g. "5411"), and the halls themselves ("Hall 2") are whole floor
    // areas rather than stands — both are noise in a browse list, so they're
    // left out here. Typing still finds them (the searchDirectory branch above).
    const all = activeCategory ? byCategory(directory, activeCategory) : directory;
    const base = all.filter((e) => !/^\d+$/.test(e.name.trim()) && !/^hall\s*\d+$/i.test(e.name.trim()));
    const exits = name === "to" && !activeCategory ? exitDirectory : [];
    renderSuggestionList(base, base.length, name, exits);

    // Then re-show nearest first, with distances: a category's places, and
    // the exits. "All Stands" (~280) stays A–Z — too many routes to measure.
    const category = activeCategory;
    void Promise.all([
      category ? sortByNearest(base) : Promise.resolve(null),
      sortByNearest(exits),
    ]).then(([stands, sortedExits]) => {
      const stillShowing = activeField === name && fields[name].input.value === "" && activeCategory === category;
      if (!stillShowing || suggestionsEl.hidden) return;
      const distances = new Map([...(stands?.distances ?? []), ...sortedExits.distances]);
      renderSuggestionList(stands?.sorted ?? base, base.length, name, sortedExits.sorted, distances);
    });
  }
}

/** "45 m", or "1.2 km" from 1000 m up. */
function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
}

/** Walking-route distances, cached per start→destination (not for a live GPS start, which moves). */
const routeDistanceCache = new Map<string, number | null>();

async function routeDistance(from: RouteOrigin, to: RouteOrigin): Promise<number | null> {
  const key = `${from.id}>${to.id}`;
  const cacheable = !isLiveLocationAnchor(from);
  if (cacheable && routeDistanceCache.has(key)) return routeDistanceCache.get(key)!;
  const directions = await mapView
    .getDirections(routeOriginTarget(from), routeOriginTarget(to), { accessible: false })
    .catch(() => undefined);
  const distance = directions ? directions.distance : null;
  if (cacheable) routeDistanceCache.set(key, distance);
  return distance;
}

/**
 * Orders `items` nearest first by real walking distance from the current
 * start (this kiosk unless the visitor picked another From); ones with no
 * route keep their order at the end. Returns the distances too, for display.
 */
async function sortByNearest<T extends RouteOrigin>(items: T[]): Promise<{ sorted: T[]; distances: Map<string, number> }> {
  const from = selection.from ?? kioskEntry;
  const distances = new Map<string, number>();
  if (!from || items.length === 0) return { sorted: items, distances };
  const measured = await Promise.all(items.map((item) => routeDistance(from, item)));
  items.forEach((item, i) => {
    const d = measured[i];
    if (d !== null) distances.set(item.id, d);
  });
  const sorted = items
    .map((item, index) => ({ item, index, d: distances.get(item.id) }))
    .sort((a, b) => (a.d ?? Infinity) - (b.d ?? Infinity) || a.index - b.index)
    .map((x) => x.item);
  return { sorted, distances };
}

/**
 * Whether a field showing `term` still shows `origin`. This kiosk is shown
 * with its suffix (`kioskFromLabel`, e.g. "Hall 2 KIOSK-2"), not its bare
 * name — comparing to the name alone dropped the default From selection the
 * moment the field was focused, leaving Get Directions disabled.
 */
function showsOrigin(origin: RouteOrigin, term: string): boolean {
  return term === origin.name || (origin === kioskEntry && term === kioskFromLabel(origin));
}

/** The stand (or, for From, this kiosk) whose name is exactly `term`, ignoring case and spacing. */
function exactEntryMatch(name: FieldName, term: string): RouteOrigin | null {
  const norm = (n: string) => n.trim().replace(/\s+/g, " ").toLowerCase();
  const wanted = norm(term);
  if (!wanted) return null;
  if (name === "from" && kioskEntry && (norm(kioskEntry.name) === wanted || norm(kioskFromLabel(kioskEntry)) === wanted)) {
    return kioskEntry;
  }
  const exit = name === "to" ? exitDirectory.find((e) => norm(e.name) === wanted) : undefined;
  return directory.find((e) => norm(e.name) === wanted) ?? exit ?? null;
}

/**
 * "From" offers a pinned tile above the stand listing. On a shared-route
 * link (`?from=&to=`, a visitor's phone) that's "Current Location" — they're
 * often standing somewhere mid-floor, not at any one exhibitor's stand, so
 * routing needs a live-GPS starting point (see `selectCurrentLocation`). On
 * the kiosk itself (no from/to in the URL) GPS is meaningless for a fixed
 * device, so the tile is this kiosk's own hall instead, letting a visitor
 * get back to it after picking some other start.
 */
function renderSuggestionList(
  results: StandEntry[],
  limit = 8,
  fieldName: FieldName = activeField,
  exits: ExitAnchor[] = [],
  /** Walking distance (m) from the start, by entry id — shown on each row when given. See `sortByNearest`. */
  distances?: Map<string, number>,
): void {
  suggestionsEl.innerHTML = "";

  let pinnedTile: HTMLLIElement | null = null;
  if (fieldName === "from") {
    if (!isSharedRouteUrl()) {
      if (kioskEntry) {
        pinnedTile = buildKioskLocationSuggestionItem(kioskEntry);
        results = results.filter((e) => !isKioskOwnEntry(e, kioskEntry!));
      }
    } else if ("geolocation" in navigator) {
      pinnedTile = buildCurrentLocationSuggestionItem();
    }
  }
  if (pinnedTile) suggestionsEl.appendChild(pinnedTile);

  suggestionsEl.hidden = results.length === 0 && exits.length === 0 && !pinnedTile;

  // Sorted by distance (see `sortByNearest`): say so above the list, and tag the closest.
  const shownStands = results.slice(0, limit);
  const nearestStandId = shownStands.find((e) => distances?.has(e.id))?.id;
  if (nearestStandId) suggestionsEl.appendChild(buildListHeading(t("nearestFirstFrom", { name: currentStartLabel() })));

  for (const entry of shownStands) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "suggestion";

    if (entry.iconUrl) {
      btn.classList.add("has-thumb");
      const thumb = document.createElement("img");
      thumb.className = "suggestion-thumb";
      // A categorized stand's icon is usually a category glyph — padded so it doesn't touch the border.
      if (entry.categoryNames.length > 0) thumb.classList.add("has-category");
      thumb.src = entry.iconUrl;
      thumb.alt = "";
      btn.appendChild(thumb);
    }

    const textCol = document.createElement("span");
    textCol.className = "suggestion-text";
    btn.appendChild(textCol);

    const title = document.createElement("span");
    title.className = "suggestion-name";
    title.textContent = entry.name;
    if (entry.id === nearestStandId) title.appendChild(buildNearestTag());
    textCol.appendChild(title);

    const occupancy = occupancyService.get(entry.space.id);
    if (occupancy) {
      const dot = document.createElement("span");
      dot.className = `suggestion-occupancy occupancy-${occupancy.level}`;
      dot.title = occupancyLabel(occupancy.level);
      title.appendChild(dot);
    }

    const distance = distances?.get(entry.id);
    const metaParts = [...entry.categoryNames, ...(distance !== undefined ? [formatDistance(distance)] : [])];
    if (metaParts.length > 0) {
      const meta = document.createElement("span");
      meta.className = "suggestion-meta";
      meta.textContent = metaParts.join(" · ");
      textCol.appendChild(meta);
    }

    wireTapToSelect(btn, () => selectEntry(activeField, entry));

    li.appendChild(btn);
    suggestionsEl.appendChild(li);
  }

  // "Nearest exit" leads the exits whenever they're listed.
  if (exits.length > 0 && config.showNearestExit) suggestionsEl.appendChild(buildNearestExitSuggestionItem());
  const nearestExitId = exits.find((e) => distances?.has(e.id))?.id;
  if (nearestExitId) suggestionsEl.appendChild(buildListHeading(t("exitsNearestFirst")));
  for (const exit of exits) {
    suggestionsEl.appendChild(buildExitSuggestionItem(exit, distances?.get(exit.id), exit.id === nearestExitId));
  }
}

/** Listing row that routes to whichever exit is closest — see `goToNearestExit`. */
function buildNearestExitSuggestionItem(): HTMLLIElement {
  const li = document.createElement("li");
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "suggestion has-thumb suggestion-nearest-exit";

  const icon = document.createElement("span");
  icon.className = "suggestion-exit-icon";
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;
  btn.appendChild(icon);

  const textCol = document.createElement("span");
  textCol.className = "suggestion-text";
  const title = document.createElement("span");
  title.className = "suggestion-name";
  title.textContent = t("nearestExit");
  const meta = document.createElement("span");
  meta.className = "suggestion-meta";
  meta.textContent = t("nearestExitHint");
  textCol.append(title, meta);
  btn.appendChild(textCol);

  wireTapToSelect(btn, () => void goToNearestExit());

  li.appendChild(btn);
  return li;
}

/**
 * One tap to the closest way out: measures the real walking route from the
 * current start (this kiosk unless the visitor picked another From) to every
 * emergency exit, makes the shortest the destination, and starts the route
 * right away — no Get Directions press, no countdown.
 */
async function goToNearestExit(): Promise<void> {
  if (nearestExitBtn.classList.contains("is-busy")) return;
  if (!selection.from) applyDefaultFromSelection();
  const from = selection.from;
  if (!from) {
    showError(t("chooseStartForExit"));
    return;
  }
  cancelAutoRoute();
  closeSearchPicker();
  nearestExitBtn.classList.add("is-busy");
  try {
    const nearest = await findNearestExit(mapView, routeOriginTarget(from), exitDirectory);
    if (!nearest) {
      showError(t("noExitRoute"));
      return;
    }
    selection.to = nearest.exit;
    fields.to.input.value = nearest.exit.name;
    fields.to.clear.hidden = false;
    updateGoButton();
    // An exit route stays flat (2D) start to finish — the clearest view of the way out.
    await calculateAndShowRoute({ stay2D: true });
  } finally {
    nearestExitBtn.classList.remove("is-busy");
  }
}

/** A listing row for an emergency exit — red exit icon, "Emergency exit" underneath. */
/** Small non-tappable heading row inside the listing, e.g. "Nearest first · from Hall 2". */
function buildListHeading(text: string): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "suggestion-heading";
  li.setAttribute("role", "presentation");
  li.textContent = text;
  return li;
}

/** Green "Nearest" tag after the closest row's name. */
function buildNearestTag(): HTMLSpanElement {
  const tag = document.createElement("span");
  tag.className = "suggestion-nearest-tag";
  tag.textContent = t("nearestTag");
  return tag;
}

/** Where distances are measured from, as the From field shows it ("Hall 2", "Current location", a stand). */
function currentStartLabel(): string {
  const from = selection.from ?? kioskEntry;
  if (!from) return "";
  return from === kioskEntry ? (config.kioskName || kioskEntry.name) : from.name;
}

function buildExitSuggestionItem(exit: ExitAnchor, distance?: number, isNearest = false): HTMLLIElement {
  const li = document.createElement("li");
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "suggestion has-thumb";

  const icon = document.createElement("span");
  icon.className = "suggestion-exit-icon";
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`;
  btn.appendChild(icon);

  const textCol = document.createElement("span");
  textCol.className = "suggestion-text";
  const title = document.createElement("span");
  title.className = "suggestion-name";
  title.textContent = exit.name;
  if (isNearest) title.appendChild(buildNearestTag());
  const meta = document.createElement("span");
  meta.className = "suggestion-meta";
  meta.textContent = [t("emergencyExit"), ...(distance !== undefined ? [formatDistance(distance)] : [])].join(" · ");
  textCol.append(title, meta);
  btn.appendChild(textCol);

  wireTapToSelect(btn, () => selectExit(exit));

  li.appendChild(btn);
  return li;
}

/** Makes an exit the destination — from the listing or a tap on its sign on the map. */
function selectExit(exit: ExitAnchor): void {
  selection.to = exit;
  fields.to.input.value = exit.name;
  fields.to.clear.hidden = false;
  closeSearchPicker();
  updateGoButton();
  scheduleAutoRoute();
}

function buildCurrentLocationSuggestionItem(): HTMLLIElement {
  const li = document.createElement("li");
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "suggestion has-thumb";

  const icon = document.createElement("span");
  icon.className = "suggestion-current-location-icon";
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>`;
  btn.appendChild(icon);

  const title = document.createElement("span");
  title.className = "suggestion-name";
  title.textContent = t("currentLocation");
  btn.appendChild(title);

  wireTapToSelect(btn, () => selectCurrentLocation());

  li.appendChild(btn);
  return li;
}

/** True when the URL is a shared route (`?from=&to=`) rather than the kiosk's own plain URL. */
function isSharedRouteUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return Boolean((params.get("from") || params.get("kiosk")) && params.get("to"));
}

/** "From" label for this kiosk's own spot — e.g. "Hall 2 (This Kiosk)". */
function kioskFromLabel(entry: RouteOrigin): string {
  const kioskSuffix = config.kioskName || config.kioskLocationExternalId || t("thisKioskSuffix");
  return `${entry.name} ${kioskSuffix}`.trim();
}

/**
 * True for a listing entry that's just this kiosk's own spot again — the
 * same stand, or one named like the kiosk (e.g. a "Hall 2" space for a
 * "Hall 2" kiosk) — so it's left out below the pinned kiosk tile.
 */
function isKioskOwnEntry(entry: StandEntry, kiosk: RouteOrigin): boolean {
  if (entry.id === kiosk.id) return true;
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
  const name = norm(entry.name);
  return [config.kioskName ?? "", kiosk.name, kioskFromLabel(kiosk)].some((n) => n.trim() && norm(n) === name);
}

function buildKioskLocationSuggestionItem(entry: RouteOrigin): HTMLLIElement {
  const li = document.createElement("li");
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "suggestion has-thumb";

  const icon = document.createElement("span");
  icon.className = "suggestion-current-location-icon";
  icon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s7-6.2 7-12a7 7 0 0 0-14 0c0 5.8 7 12 7 12z"/><circle cx="12" cy="10" r="2.5"/></svg>`;
  btn.appendChild(icon);

  const title = document.createElement("span");
  title.className = "suggestion-name";
  // "Current Hall 2" here; the From field itself still shows just "Hall 2".
  title.textContent = t("currentKioskLocation").replace("{name}", kioskFromLabel(entry));
  btn.appendChild(title);

  wireTapToSelect(btn, () => {
    applyDefaultFromSelection();
    closeSearchPicker();
    scheduleAutoRoute();
  });

  li.appendChild(btn);
  return li;
}

/**
 * Sets "From" to the visitor's live GPS fix instead of any stand — for
 * someone standing in an aisle or open area rather than at a named
 * exhibitor. One-shot (not a continuous watch like `startLiveLocationTracking`,
 * which is reserved for the shared-QR-route phone flow): a fresh fix is
 * taken each time this tile is tapped, and the resulting Coordinate is
 * passed straight into routing as a `RouteOrigin` (see `LiveLocationAnchor`
 * in directory.ts) — Mappedin's pathfinder accepts a raw Coordinate as a
 * start point just as it does a Space or Point of Interest.
 */
function selectCurrentLocation(): void {
  if (!("geolocation" in navigator)) {
    updateLiveLocationBadge("unavailable");
    return;
  }

  updateLiveLocationBadge("acquiring");
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const floorId = mapView.currentFloor?.id;
      if (!floorId) {
        updateLiveLocationBadge("unavailable");
        return;
      }

      const coordinate = new Coordinate({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        floorId,
      });
      const anchor: RouteOrigin = {
        id: "current-location",
        name: t("currentLocation"),
        target: coordinate,
      };

      selection.from = anchor;
      fields.from.input.value = anchor.name;
      fields.from.clear.hidden = false;
      closeSearchPicker();
      updateGoButton();
      scheduleAutoRoute();
      addKioskMarker(mapView, coordinate);
      updateLiveLocationBadge("active", Math.round(position.coords.accuracy));
    },
    (err) => {
      updateLiveLocationBadge(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable");
    },
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
  );
}

/**
 * Fires `onTap` only for a genuine tap — not a touch that turns into a
 * scroll of the (overflow-y: auto) suggestions list. `pointerdown` fires
 * the instant a finger touches down, before any scroll gesture can be
 * recognized, so selecting on pointerdown (as this used to) hijacked every
 * scroll attempt into an instant selection. Tracking movement and only
 * acting on `pointerup` — without ever calling `preventDefault` on the
 * touch — lets the browser's native scrolling win when the finger drags.
 */
function wireTapToSelect(el: HTMLElement, onTap: () => void): void {
  const MOVE_THRESHOLD_PX = 10;
  let startX = 0;
  let startY = 0;
  let moved = false;

  el.addEventListener("pointerdown", (e) => {
    startX = e.clientX;
    startY = e.clientY;
    moved = false;
  });

  el.addEventListener("pointermove", (e) => {
    if (Math.abs(e.clientX - startX) > MOVE_THRESHOLD_PX || Math.abs(e.clientY - startY) > MOVE_THRESHOLD_PX) {
      moved = true;
    }
  });

  el.addEventListener("pointerup", () => {
    if (!moved) onTap();
  });
}

/**
 * Fires `onLongPress` after the pointer holds still on `el` for `holdMs`.
 * Cancelled by releasing early or dragging past the movement threshold —
 * a normal quick tap never triggers it. Also swallows the `click` that a
 * browser fires on release after a long-press-then-let-go, so the same
 * gesture doesn't *also* trigger whatever `click` listener the caller
 * separately attaches to `el`.
 */
function wireLongPress(el: HTMLElement, onLongPress: () => void, holdMs = 600): void {
  const MOVE_THRESHOLD_PX = 10;
  let startX = 0;
  let startY = 0;
  let timer: number | undefined;
  let fired = false;

  const cancelTimer = () => {
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timer = undefined;
    }
  };

  el.addEventListener("pointerdown", (e) => {
    startX = e.clientX;
    startY = e.clientY;
    fired = false;
    cancelTimer();
    timer = window.setTimeout(() => {
      fired = true;
      onLongPress();
    }, holdMs);
  });

  el.addEventListener("pointermove", (e) => {
    if (Math.abs(e.clientX - startX) > MOVE_THRESHOLD_PX || Math.abs(e.clientY - startY) > MOVE_THRESHOLD_PX) {
      cancelTimer();
    }
  });

  el.addEventListener("pointerup", cancelTimer);
  el.addEventListener("pointercancel", cancelTimer);
  el.addEventListener("pointerleave", cancelTimer);

  el.addEventListener(
    "click",
    (e) => {
      if (fired) {
        e.stopImmediatePropagation();
        e.preventDefault();
        fired = false;
      }
    },
    true,
  );
}

/** Syncs the lock button's icon/label and the rotation-driving controls to `isCameraLocked`. Shared by the manual toggle and the boot-time auto-lock (VITE_DEFAULT_CAMERA_LOCK). */
function applyLockUiState(): void {
  lockBtn.setAttribute("aria-pressed", String(isCameraLocked));
  lockBtn.classList.toggle("active", isCameraLocked);
  lockBtn.title = isCameraLocked ? t("unlockRotation") : t("lockRotation");
  lockBtn.querySelector<SVGElement>(".lock-icon-closed")!.toggleAttribute("hidden", !isCameraLocked);
  lockBtn.querySelector<SVGElement>(".lock-icon-open")!.toggleAttribute("hidden", isCameraLocked);

  // Rotate/compass buttons drive bearing programmatically, bypassing the
  // gesture-only interaction lock above, so the whole rotation block is
  // collapsed while locked. It clips (`overflow: hidden`) only during the
  // height animation; once expanded the clip is lifted so each button's
  // own shadow renders fully instead of one wrapped block.
  mapControlsEl.classList.toggle("rotation-locked", isCameraLocked);
  if (isCameraLocked) {
    rotateControlsEl.style.overflow = "hidden";
  } else if (rotateControlsEl.style.overflow === "hidden") {
    const revealShadows = (): void => {
      rotateControlsEl.style.overflow = "visible";
      rotateControlsEl.removeEventListener("transitionend", revealShadows);
    };
    rotateControlsEl.addEventListener("transitionend", revealShadows);
  }
}

/** Reveals the hidden rotation-lock button and (re)starts its 10s auto-hide timer. */
function revealLockBtn(): void {
  lockBtn.classList.add("revealed");
  if (lockBtnHideTimer !== undefined) window.clearTimeout(lockBtnHideTimer);
  lockBtnHideTimer = window.setTimeout(hideLockBtn, 10_000);
}

/** Hides the rotation-lock button again — a tap on it while hidden is a no-op. */
function hideLockBtn(): void {
  lockBtn.classList.remove("revealed");
  if (lockBtnHideTimer !== undefined) {
    window.clearTimeout(lockBtnHideTimer);
    lockBtnHideTimer = undefined;
  }
}

function selectEntry(name: FieldName, entry: StandEntry): void {
  selection[name] = entry;
  fields[name].input.value = entry.name;
  fields[name].clear.hidden = false;
  closeSearchPicker();
  updateGoButton();
  scheduleAutoRoute();
}

function swapFields(): void {
  // A live GPS fix only ever makes sense as a starting point, not a
  // destination to route "to" — block the swap rather than moving it there.
  if (isLiveLocationAnchor(selection.from) || isLiveLocationAnchor(selection.to)) return;

  const from = selection.from;
  const to = selection.to;
  selection.from = to;
  selection.to = from;
  fields.from.input.value = to?.name ?? "";
  fields.to.input.value = from?.name ?? "";
  fields.from.clear.hidden = !to;
  fields.to.clear.hidden = !from;
  updateGoButton();
  cancelAutoRoute();

  swapBtn.classList.remove("spinning");
  void swapBtn.offsetWidth;
  swapBtn.classList.add("spinning");
  swapBtn.addEventListener(
    "animationend",
    () => swapBtn.classList.remove("spinning"),
    { once: true },
  );
}

function updateGoButton(): void {
  goBtn.disabled = !selection.from || !selection.to;
  syncBottomSearch();
}

/**
 * Visitors often miss the From/To panel, so a big search bar sits at the
 * bottom centre. It only feeds "To" (From stays this kiosk): a tap opens the
 * To field with its keyboard and listing, exactly as tapping To itself does.
 */
function wireBottomSearch(): void {
  if (!config.showBottomSearch) return;
  let flying = false;
  bottomSearchBtn.addEventListener("click", () => {
    if (flying) return; // a second tap mid-flight shouldn't start another
    flying = true;
    void flyBottomSearchToField().then(() => {
      flying = false;
      fields.to.input.focus();
      // Already focused (no `focus` event) — open the picker directly.
      if (!searchPickerOpen) openFieldPicker("to");
    });
  });
  // Out of the way while a stand's card is showing.
  new MutationObserver(syncBottomSearch).observe(placeCard, { attributes: true, attributeFilter: ["hidden"] });
  syncBottomSearch();
}

/**
 * The bar glides up into the To field it feeds (shrinking to the field's
 * size and fading), so the visitor's eye follows it to where they'll type.
 * Resolves when it's there; instantly for reduced-motion users.
 */
async function flyBottomSearchToField(): Promise<void> {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const from = bottomSearchBtn.getBoundingClientRect();
  const to = toFieldEl.getBoundingClientRect();
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  const animation = bottomSearchBtn.animate(
    [
      { transform: "none", opacity: 1 },
      { transform: `translate(${dx * 0.85}px, ${dy * 0.85}px) scale(${(to.width / from.width) * 1.1}, ${(to.height / from.height) * 1.1})`, opacity: 0.9, offset: 0.8 },
      { transform: `translate(${dx}px, ${dy}px) scale(${to.width / from.width}, ${to.height / from.height})`, opacity: 0 },
    ],
    { duration: 520, easing: "cubic-bezier(0.45, 0, 0.2, 1)", fill: "forwards" },
  );
  await animation.finished.catch(() => undefined);
  // Opening the picker hides the bar; drop the end pose once it's hidden so
  // it comes back in its own place next time.
  requestAnimationFrame(() => animation.cancel());
}

/** Shows the bar unless the picker or a place card is open, with the chosen destination as its text. */
function syncBottomSearch(): void {
  if (!config.showBottomSearch) return;
  bottomSearchBtn.hidden = searchPickerOpen || !placeCard.hidden;
  const toName = selection.to?.name ?? "";
  bottomSearchValueEl.textContent = toName;
  bottomSearchValueEl.hidden = !toName;
  bottomSearchPlaceholderEl.hidden = Boolean(toName);
}

/**
 * Some visitors pick From/To from the list and never press Get Directions.
 * Once both are set by a list pick, start the route by itself after
 * VITE_AUTO_ROUTE_SECONDS — the button fills up as a countdown meanwhile.
 * Going back into a field, swapping, pressing Go, or Reset cancels it.
 */
function scheduleAutoRoute(): void {
  cancelAutoRoute();
  if (config.autoRouteDelayMs <= 0 || !selection.from || !selection.to) return;
  goBtn.style.setProperty("--auto-route-ms", `${config.autoRouteDelayMs}ms`);
  void goBtn.offsetWidth; // restart the fill animation on a re-pick
  goBtn.classList.add("auto-routing");
  autoRouteTimer = window.setTimeout(() => {
    autoRouteTimer = undefined;
    goBtn.classList.remove("auto-routing");
    if (!goBtn.disabled) void calculateAndShowRoute();
  }, config.autoRouteDelayMs);
}

function cancelAutoRoute(): void {
  if (autoRouteTimer !== undefined) {
    window.clearTimeout(autoRouteTimer);
    autoRouteTimer = undefined;
  }
  goBtn.classList.remove("auto-routing");
}

goBtn.addEventListener("click", () => {
  void calculateAndShowRoute();
});

async function calculateAndShowRoute(
  options: { anchorKioskMarkerToRouteStart?: boolean; silent?: boolean; stay2D?: boolean } = {},
): Promise<void> {
  if (!selection.from || !selection.to) return;
  // `silent`: a background refresh of a route already on screen (see
  // `startLiveRouteRefresh`) — no panel closing, no button spinner, no
  // camera flythrough, and the steps list keeps its expanded/collapsed state.
  const silent = options.silent === true;
  if (!silent) cancelAutoRoute();
  if (!silent) {
    closeSearchPicker();
    closePlaceCard();
  }
  const stepsWereExpanded = resultSection.classList.contains("steps-expanded");
  goBtn.disabled = true;

  const goText = goBtn.querySelector("span")!;
  const goSpinner = goBtn.querySelector(".go-spinner") as HTMLElement;
  if (!silent) {
    goText.hidden = true;
    goSpinner.hidden = false;
  }

  try {
    // Placed before routing: the route intro walks this same pin along the
    // path and parks it back on the stand (only one destination pin ever shows).
    setDestinationMarker(
      mapView,
      routeOriginTarget(selection.to),
      selection.to.name,
      isStandEntry(selection.to) ? selection.to.iconUrl : undefined,
    );

    const result = await routeBetween(mapView, routeOriginTarget(selection.from), routeOriginTarget(selection.to), {
      intro: !silent,
      stay2D: options.stay2D,
    });

    let alongRoutePOIs: AlongRoutePOI[] = [];
    if (result.status === "found" && poiDirectory.length > 0) {
      const allPOIs = poiDirectory.map((e) => ({
        poi: e.poi,
        category: getCategoryLabel(e.category),
        color: getCategoryColor(e.category),
        svg: getCategorySVG(e.category),
      }));
      alongRoutePOIs = findPOIsAlongRoute(mapData, result.directions, allPOIs);
    }

    renderResult(result, selection.from, selection.to, alongRoutePOIs);
    if (silent && stepsWereExpanded) setStepsExpanded(true);

    // Only for a route restored from a shared QR link (see
    // `restoreSharedRoute`): re-anchor "You Are Here" to the route's actual
    // starting point instead of the From space's centroid, since the
    // room's center often sits away from the door/aisle edge the route
    // line itself starts from. The kiosk's own routing never does this —
    // its marker stays at the device's exact configured location.
    // A "Current location" start is a raw GPS point, which sits off the walkable
    // path — put the dot on the route line itself, same as a shared route.
    if (result.status === "found" && (options.anchorKioskMarkerToRouteStart || isLiveLocationAnchor(selection.from))) {
      addKioskMarker(mapView, result.directions.coordinates[0]);
    }

    // A route that starts from the visitor's own GPS keeps itself current.
    if (!silent) {
      if (result.status === "found" && isLiveLocationAnchor(selection.from)) startLiveRouteRefresh();
      else stopLiveRouteRefresh();
    }
  } catch (err) {
    console.error(err);
    showError(t("couldNotCalculateDirections"));
  } finally {
    goBtn.disabled = false;
    if (!silent) {
      goText.hidden = false;
      goSpinner.hidden = true;
    }
  }
}

let liveRouteRefreshTimer: number | undefined;
let liveRouteRefreshBusy = false;

/**
 * Every `config.liveRouteRefreshMs` (default 10 s), while the visible route
 * starts from "Current location": take a fresh GPS fix and recalculate the
 * route from it, so the line and the "You Are Here" dot follow the visitor
 * even when they walk away from the original path. A fix that is too rough
 * (> 50 m), that failed, or that barely moved (< 3 m) is skipped, so the
 * route doesn't redraw for GPS noise. Stops by itself once the route is
 * cleared or "From" is no longer the live location.
 */
function startLiveRouteRefresh(): void {
  stopLiveRouteRefresh();
  if (config.liveRouteRefreshMs <= 0 || !("geolocation" in navigator)) return;

  liveRouteRefreshTimer = window.setInterval(() => {
    const from = selection.from;
    if (!from || !selection.to || !isLiveLocationAnchor(from) || resultSection.hidden) {
      stopLiveRouteRefresh();
      return;
    }
    if (liveRouteRefreshBusy) return;
    liveRouteRefreshBusy = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const current = selection.from;
        const floorId = mapView.currentFloor?.id;
        if (!current || !isLiveLocationAnchor(current) || !floorId || position.coords.accuracy > 50) {
          liveRouteRefreshBusy = false;
          return;
        }
        const coordinate = new Coordinate({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          floorId,
        });
        if (metersBetween(current.target, coordinate) < 3) {
          liveRouteRefreshBusy = false;
          return;
        }
        selection.from = { id: "current-location", name: current.name, target: coordinate };
        updateLiveLocationBadge("active", Math.round(position.coords.accuracy));
        void calculateAndShowRoute({ silent: true }).finally(() => {
          liveRouteRefreshBusy = false;
        });
      },
      () => {
        liveRouteRefreshBusy = false;
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 8000 },
    );
  }, config.liveRouteRefreshMs);
}

function stopLiveRouteRefresh(): void {
  window.clearInterval(liveRouteRefreshTimer);
  liveRouteRefreshTimer = undefined;
  liveRouteRefreshBusy = false;
}

function renderResult(
  result: Awaited<ReturnType<typeof routeBetween>>,
  from: RouteOrigin,
  to: RouteOrigin,
  alongRoutePOIs: AlongRoutePOI[] = [],
): void {
  resultSection.hidden = false;
  resultInstructionsEl.innerHTML = "";
  setStepsExpanded(false);
  alongRouteListEl.innerHTML = "";
  alongRouteSection.hidden = true;
  // Only a real route is worth sharing — not a "no route found" message —
  // and only when both ends are full stand entries: a shared link encodes
  // Space ids the recipient's phone resolves via the directory (see
  // restoreSharedRoute), which a bare kiosk Point-of-Interest anchor (see
  // findKioskAnchor) has no equivalent for.
  shareRouteBtn.hidden = !config.showQrShare || result.status !== "found" || !isShareableRoute(from, to);
  // A result exists now — collapse the picker back down so a phone screen
  // shows just the directions instead of both stacked at once.
  setPickerCollapsed(true);

  if (result.status === "same-stand") {
    resultDistanceEl.textContent = t("alreadyAt", { name: to.name });
    resultStepsEl.innerHTML = "";
    resultEtaEl.innerHTML = "";
    return;
  }

  if (result.status === "no-route") {
    resultDistanceEl.textContent = t("noRouteFound");
    resultStepsEl.innerHTML = t("checkConnectedAisles");
    resultEtaEl.innerHTML = "";
    return;
  }

  resultDistanceEl.textContent = t("metersSuffix", { n: Math.round(result.distanceMeters) });
  resultStepsEl.innerHTML = t("stepsApprox", { n: result.steps });
  resultEtaEl.innerHTML = `\u23F1 ${t("minWalk", { n: result.etaMinutes })}`;

  const detailed = buildDetailedInstructions(result.directions.instructions);
  for (const step of detailed) {
    const li = document.createElement("li");
    li.className = "instruction-card";

    const iconBox = document.createElement("div");
    iconBox.className = "instruction-icon";
    iconBox.innerHTML = getDirectionSvg(step.icon);
    li.appendChild(iconBox);

    const textBox = document.createElement("div");
    textBox.className = "instruction-text";
    textBox.textContent = step.text;
    li.appendChild(textBox);

    resultInstructionsEl.appendChild(li);
  }

  if (alongRoutePOIs.length > 0) {
    // Kept in the codebase but intentionally never revealed: the
    // along-the-route "On Your Way" amenity strip stays hidden from
    // the kiosk UI. The compute + render helpers stay for future use.
    // alongRouteSection.hidden = false;  // (stays hidden, never shown)
    for (const entry of alongRoutePOIs) {
      const card = document.createElement("div");
      card.className = "along-route-card";
      card.style.setProperty("--along-poi-color", entry.color);

      card.innerHTML = `
        <div class="along-route-card-top">
          <div class="along-route-poi-icon">${entry.svg}</div>
          <span class="along-route-poi-name">${entry.poi.name || entry.category}</span>
        </div>
        <span class="along-route-poi-category">${entry.category}</span>
        <span class="along-route-detour">${t("detourSuffix", { n: entry.detourTimeSeconds })}</span>
      `;

      card.addEventListener("click", () => {
        focusOnPOI(mapView, entry.poi);
      });

      alongRouteListEl.appendChild(card);
    }
  }
}

function getDirectionSvg(type: DirectionIconType): string {
  switch (type) {
    case "turn-left":
    case "slight-left":
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v5.5"/></svg>`;
    case "turn-right":
    case "slight-right":
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 14l5-5-5-5"/><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5V20"/></svg>`;
    case "u-turn":
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 10 4 15 9 20"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>`;
    case "elevator":
    case "escalator":
    case "stairs":
    case "connection":
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"/><polyline points="7 6 12 11 17 6"/></svg>`;
    case "arrival":
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
    case "depart":
    case "straight":
    default:
      return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;
  }
}

// --- Reset / Idle ------------------------------------------------------------

function resetAll(): void {
  cancelAutoRoute();
  clearRoute(mapView);
  clearDestinationMarker(mapView);
  clearPOIMarkers(mapView);
  hideLockBtn(); // the operator control goes back into hiding for the next visitor

  // "From" is about to reset away from a visitor's live GPS fix (if that's
  // what it was) — put "You Are Here" back at the kiosk's own fixed spot
  // and drop the GPS accuracy badge along with it.
  if (isLiveLocationAnchor(selection.from)) {
    liveLocationBadgeEl.hidden = true;
    if (kioskEntry) addKioskMarker(mapView, routeOriginTarget(kioskEntry));
  }

  if (isCameraLocked) {
    // A locked camera means the operator pinned the kiosk to a fixed
    // framing on purpose. Pan/zoom stay usable while locked, so a visitor
    // could still have drifted away from that framing — reset should bring
    // the camera back to exactly the pose it was locked at, not the
    // generic default overview.
    applyCameraPreset(mapView, mapData, "locked-view", 1200);
  } else {
    applyCameraPreset(mapView, mapData, "default-view", 1200);

    // The default-view preset is captured in 3D — keep the tilt toggle in sync.
    is3DMode = true;
    tiltLabel.textContent = "2D";
    tiltBtn.classList.add("active");
  }

  resultSection.hidden = true;
  shareRouteBtn.hidden = true;
  closeQrPopover();
  setPickerCollapsed(true);
  closeSearchPicker();
  closePlaceCard();

  if (activeCategory !== null) {
    activeCategory = null;
    highlightZone(mapView, allVenueSpaces, null);
    renderCategoryChips();
  }

  selection.to = null;
  fields.to.input.value = "";
  fields.to.clear.hidden = true;

  applyDefaultFromSelection();
}

/**
 * Plays the intro video full screen over the map while nobody is using
 * the kiosk. The source is only attached on
 * first idle so a visitor's session never pays for loading it. A missing
 * or broken file just leaves the normal screen showing.
 */
function showIdleVideo(): void {
  if (!config.idleVideoUrl) return;
  if (!idleVideo.getAttribute("src")) {
    idleVideo.addEventListener("error", hideIdleVideo);
    idleVideo.src = config.idleVideoUrl;
  }
  idleVideo.currentTime = 0;
  idleVideoWrap.hidden = false;
  void idleVideo.play().catch(() => {
    // Autoplay can only be refused for unmuted video; it's muted, so this
    // is a decode/network failure — fall back to the plain kiosk screen.
    hideIdleVideo();
  });
}

function hideIdleVideo(): void {
  idleVideo.pause();
  idleVideoWrap.hidden = true;
}

/**
 * Pre-fills "From" with this kiosk's own configured location (`kioskEntry`),
 * or clears it if none is known, and re-syncs the Go button. Used both by
 * `resetAll` (idle timeout / Reset tap) and once at boot, so a visitor never
 * has to manually pick "This Kiosk" as their starting point.
 */
function applyDefaultFromSelection(): void {
  if (kioskEntry) {
    selection.from = kioskEntry;
    fields.from.input.value = kioskFromLabel(kioskEntry);
    fields.from.clear.hidden = true;
  } else {
    selection.from = null;
    fields.from.input.value = "";
    fields.from.clear.hidden = true;
  }
  updateGoButton();
}

// --- Misc --------------------------------------------------------------------

function startClock(): void {
  const update = () => {
    const now = new Date();
    const date = `${now.getDate()} ${now.toLocaleString([], { month: "short" })} ${now.getFullYear()}`;
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    clockEl.textContent = `${date}, ${time}`;
  };
  update();
  setInterval(update, 15_000);
}

/** Renders the live zoom/bearing/pitch header readout from the camera's current state. */
function updateCameraDebugReadout(camera: MapView["Camera"]): void {
  const zoom = camera.zoomLevel.toFixed(1);
  const bearing = Math.round(camera.bearing);
  const pitch = Math.round(camera.pitch);
  cameraDebugEl.textContent = `Z ${zoom} · B ${bearing}° · P ${pitch}°`;
}

function showError(message: string): void {
  errorBanner.hidden = false;
  errorTextEl.textContent = message;
  setTimeout(() => {
    errorBanner.hidden = true;
  }, 6000);
}
